import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {TeamCompaniesService} from '../../../services/teamcompanies/teamcompanies.service';
import {UsersInsuranceService} from '../../../services/usersinsurance/users.insurance.service';
import {TeamCompanyUsersService} from '../../../services/teamcompanyusers/teamcompanyusers.service';
import {SnackBarComponent} from '../../snackbar/snackbar.component';
import {MatSnackBar} from '@angular/material/snack-bar';
import {EditCompanyDialogComponent, EditCompanyDialogModel} from '../../modal/editcompanydialog/editcompanydialog.component';
import {MatDialog} from '@angular/material/dialog';
import {Dictionary} from 'highcharts';
import {UsersService} from '../../../services/users/users.service';

let rememberedClientId = '';

@Component({
  selector: 'app-clients',
  templateUrl: './clients.component.html',
  styleUrls: ['./clients.component.scss']
})
export class ClientsComponent implements OnInit, OnDestroy {
  public readonly plusPlanDefaultHelpText =
    'Company setting: teams under this employer act as Plus plan teams through the company plan override.';
  public readonly includedPlusPlanHelpText =
    'When enabled, Plus Monthly plan charges are included for teams under this employer, so allowance funding does not add a separate Plus subscription fee.';

  public selectedClientId = '';
  public clientName = '';
  public clients: any[] = [];
  public clientCensusCounts: {[clientId: string]: number} = {};
  public info: any;
  public noPartner = false;
  public plusPlanEnabled = false;
  public includedPlusPlanEnabled = false;
  public isClientAdministrator = false;

  public links = [
    { title: 'Census', link: '/brokeragency/clients/client-census' },
    { title: 'Eligibility', link: '/brokeragency/clients/client-eligibility' },
    { title: 'Enrollment', link: '/brokeragency/clients/client-enrollment' }
  ];

  public activeLink: { title: string; link: string } | undefined;

  private subs: Subscription[] = [];
  private pendingRouteClientId: string | null = null;
  private clientsLoaded = false;

  constructor(
      private teamCompaniesService: TeamCompaniesService,
      private router: Router,
      private route: ActivatedRoute,
      private usersInsuranceService: UsersInsuranceService,
      private teamCompanyUsersService: TeamCompanyUsersService,
      private usersService: UsersService,
      private snackBar: MatSnackBar,
      private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    const roles = this.teamCompanyUsersService.roles || {} as any;
    this.isClientAdministrator = !!roles.client
        && !roles.manager
        && !roles.broker
        && !roles.agency
        && !roles.member
        && !roles.supportAdmin
        && !roles.supportAgent
        && !roles.teamAgent
        && !this.usersService.isAdmin();
    if (this.isClientAdministrator) {
      this.links = this.links.filter(link => link.title !== 'Enrollment');
    }
    this.selectedClientId = rememberedClientId;

    this.setActiveLinkFromUrl();

    this.subs.push(
        this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe(() => {
              this.setActiveLinkFromUrl();
            })
    );

    this.subs.push(
        this.route.paramMap.subscribe((pm) => {
          const id = pm.get('id');
          if (id) {
            this.pendingRouteClientId = id;
            this.applyClientIdFromRouteIfPossible();
          }
        })
    );

    this.teamCompaniesService
        .getCompanies(
            this.teamCompanyUsersService.roles.broker
                ? 'broker'
                : this.teamCompanyUsersService.roles.agency
                    ? 'agency'
                    : this.teamCompanyUsersService.roles.member
                        ? 'member'
                        : 'client'
        )
        .then((companies: any[]) => {
          let filtered = (companies || []).filter((company: { type: string }) => company.type === 'employer');

          if (this.teamCompanyUsersService.roles.member) {
            if (!this.teamCompanyUsersService.roles.member.metadata.clients) {
              this.snackBar.openFromComponent(SnackBarComponent, {data: 'This member is not assigned as a producer for any clients.'});
            }
            filtered = filtered.filter((company: { id: any }) => this.teamCompanyUsersService.roles.member.metadata.clients?.indexOf(company.id) >= 0);
          }

          this.clients = filtered;
          this.loadClientCensusCounts(this.clients);
          this.clientsLoaded = true;

          if (this.pendingRouteClientId) {
            if (this.setClientByIdIfValid(this.pendingRouteClientId)) {
              this.refreshDerivedClientState();
              this.ensureUrlHasClientId();
              return;
            }
          }

          if (this.selectedClientId) {
            if (this.setClientByIdIfValid(this.selectedClientId)) {
              this.refreshDerivedClientState();
              this.ensureUrlHasClientId();
              return;
            }
          }

          if (this.clients.length > 0) {
            this.setClientById(this.clients[0].id);
            this.refreshDerivedClientState();
            this.ensureUrlHasClientId();
            return;
          }

          const roleClientId = this.teamCompanyUsersService.roles.client?.id;
          if (roleClientId) {
            this.setClientById(roleClientId);
            this.refreshDerivedClientState();
            this.ensureUrlHasClientId();
          }
          this.clientName = this.teamCompanyUsersService.roles.client?.name || '';
        });

    this.subs.push(
        this.usersInsuranceService.insuranceStateChangedObservable.subscribe(() => {
          this.getCounts();
        })
    );
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
    this.subs = [];
  }

  gotoTab(linkObj: { title: string; link: string }): void {
    this.activeLink = linkObj;
    this.ensureUrlHasClientId();
  }

  setClientId(id: any): void {
    if (!id) {
      return;
    }
    const nextId = String(id);
    if (String(this.selectedClientId) === nextId) {
      return;
    }
    this.setClientById(nextId);
    this.refreshDerivedClientState();
    this.ensureUrlHasClientId();
  }

  setClient(event: any): void {
    this.setClientId(event?.value);
  }

  editCompany(): void {
    const dialogRef = this.dialog.open(EditCompanyDialogComponent, {
      data: new EditCompanyDialogModel(this.getCurrentCompany(), undefined)
    });

    dialogRef.afterClosed().subscribe(dialogResult => {
      if (!dialogResult) {
        return;
      }
      this.teamCompaniesService.uploadLogo(this.selectedClientId, dialogResult.imageData).then(result => {
        dialogResult.logoUrl = result.url;
        delete dialogResult.imageData;
        this.teamCompaniesService.modifyCompany(this.selectedClientId, dialogResult).then(() => {
          this.snackBar.openFromComponent(SnackBarComponent, {data: 'Company updated'});
        });
      });
    });
  }

  togglePayrollTeamPlusDefault(): void {
    const current = this.getCurrentCompany();
    if (!current) {
      return;
    }

    const enablePlusPlan = !this.teamCompaniesService.isCompanyPlusPlan(current);
    const nextPayrollConfig = this.teamCompaniesService.buildCompanyPlusPlanPayrollConfig(
      current,
      enablePlusPlan,
    );

    this.teamCompaniesService
        .modifyCompany(
            this.selectedClientId,
            { payrollConfig: nextPayrollConfig }
        )
        .then(() => {
          current.payrollConfig = nextPayrollConfig;
          this.plusPlanEnabled = enablePlusPlan;
          this.snackBar.openFromComponent(SnackBarComponent, {
            data: `${enablePlusPlan ? 'Enabled' : 'Disabled'} Company Plus plan for ${current.name || 'this client'}`
          });
        });
  }

  getPayrollTeamPlusDefaultActionLabel(): string {
    return `${this.plusPlanEnabled ? 'Disable' : 'Enable'} Company Plus Plan`;
  }

  toggleIncludedPlusPlan(): void {
    const current = this.getCurrentCompany();
    if (!current) {
      return;
    }

    const enableIncludedPlusPlan =
      this.teamCompaniesService.getPayrollAdminTeamsConfig(current)?.plusPlanIncluded !== true;
    const nextPayrollConfig = this.teamCompaniesService.buildPayrollAdminTeamsPayrollConfig(
      current,
      {
        plusPlanIncluded: enableIncludedPlusPlan ? true : undefined,
      },
    );

    this.teamCompaniesService
        .modifyCompany(
            this.selectedClientId,
            { payrollConfig: nextPayrollConfig }
        )
        .then(() => {
          current.payrollConfig = nextPayrollConfig;
          this.includedPlusPlanEnabled = enableIncludedPlusPlan;
          this.snackBar.openFromComponent(SnackBarComponent, {
            data: `${enableIncludedPlusPlan ? 'Enabled' : 'Disabled'} Included Plus plan for ${current.name || 'this client'}`
          });
        });
  }

  getIncludedPlusPlanActionLabel(): string {
    return `${this.includedPlusPlanEnabled ? 'Disable' : 'Enable'} Included Plus Plan (No Subscription Fee)`;
  }

  private setClientById(id: string): void {
    this.selectedClientId = String(id);
    rememberedClientId = this.selectedClientId;
  }

  private setClientByIdIfValid(id: string): boolean {
    if (!id) {
      return false;
    }
    const exists = this.clients.some(c => String(c.id) === String(id));
    if (!exists) {
      return false;
    }
    this.setClientById(id);
    return true;
  }

  private applyClientIdFromRouteIfPossible(): void {
    if (!this.pendingRouteClientId) {
      return;
    }
    if (!this.clientsLoaded) {
      return;
    }
    if (this.setClientByIdIfValid(this.pendingRouteClientId)) {
      this.refreshDerivedClientState();
      return;
    }
    if (this.clients.length > 0) {
      this.setClientById(this.clients[0].id);
      this.refreshDerivedClientState();
      this.ensureUrlHasClientId();
    }
  }

  private refreshDerivedClientState(): void {
    const currentCompany = this.getCurrentCompany();
    const payrollAdminTeamsConfig = this.teamCompaniesService.getPayrollAdminTeamsConfig(
      currentCompany,
    );
    this.plusPlanEnabled = this.teamCompaniesService.isCompanyPlusPlan(currentCompany);
    this.includedPlusPlanEnabled = payrollAdminTeamsConfig?.plusPlanIncluded === true;
    this.setNoPartner();
    this.getCounts();
  }

  private setNoPartner(): void {
    this.noPartner = !this.teamCompaniesService.getRawPayrollConfig(this.getCurrentCompany())?.partners;
  }

  private getCurrentCompany(): Dictionary<any> | undefined {
    return this.clients.find((client: { id: string }) => String(client.id) === String(this.selectedClientId));
  }

  private loadClientCensusCounts(clients: any[]): void {
    clients.forEach((client: { id: string }) => {
      this.usersInsuranceService.getCounts(client.id).then(result => {
        this.clientCensusCounts = {
          ...this.clientCensusCounts,
          [client.id]: result.census || 0,
        };
      }).catch(() => {
        this.clientCensusCounts = {
          ...this.clientCensusCounts,
          [client.id]: 0,
        };
      });
    });
  }

  private getCounts(): void {
    if (!this.selectedClientId) {
      return;
    }
    this.usersInsuranceService.getCounts(this.selectedClientId).then(result => {
      this.clientCensusCounts = {
        ...this.clientCensusCounts,
        [this.selectedClientId]: result.census || 0,
      };
      const items = [
        {title: 'Census', value: result.census, background: 'lightskyblue'},
        {title: 'Eligible', value: result.eligible, background: 'orange'},
        {title: 'Enrolled', value: result.enrolled, background: 'mediumturquoise'},
        {title: 'Invited', value: result.invited, background: 'mediumorchid'},
        {title: 'Signed Up', value: result.signedup, background: 'yellowgreen'},
      ];
      this.info = this.isClientAdministrator ? items.slice(0, 2) : items;
    });
  }

  private setActiveLinkFromUrl(): void {
    const path = this.router.url.split('?')[0];
    const parts = path.split('/').filter(Boolean);

    // links are /brokeragency/clients/client-census (3 segments after root)
    // urls may be /brokeragency/clients/client-census/123, so drop id
    const base = parts.length >= 3 ? `/${parts.slice(0, 3).join('/')}` : `/${parts.join('/')}`;

    this.activeLink = this.links.find(l => l.link === base) || this.links[0];
  }

  private ensureUrlHasClientId(): void {
    const linkObj = this.activeLink || this.links[0];

    if (!this.selectedClientId) {
      this.router.navigateByUrl(linkObj.link);
      return;
    }

    const target = `${linkObj.link}/${this.selectedClientId}`;
    const current = this.router.url.split('?')[0];

    if (current === target) {
      return;
    }

    this.router.navigateByUrl(target);
  }
}
